/**
 * Sets — set times as a first-class page, beside Events/Map/Find.
 *
 * Two screens share this component, on the Maps page's model:
 *   /sets            — the INDEX: the upcoming festivals you are going to that have a lineup,
 *                      soonest first, each row saying whether its set times
 *                      have landed.
 *   /sets/:eventId   — the SCHEDULE for one festival.
 *
 * What a festival has published decides what this shows, in this order:
 *   1. set times      -> the schedule grid (stage columns, time rail)
 *   2. stages, no times -> the lineup by stage
 *   3. days, no stages  -> the lineup by day
 *   4. neither          -> the lineup
 * A festival announces its lineup months out and its set times days out. On
 * 2026-09-22, of the ten festivals in the window, only Outside Lands had times;
 * CRSSD had stages and Portola had days. Degrading is the normal case here, not
 * the error case, so each level says plainly what is still to come rather than
 * showing an empty grid.
 *
 * Your group rides along (operator, 2026-09-23): when you are in a group for the
 * festival, every artist carries who in it wants to see them ("3/3"), the day
 * opens on the group's top picks, and tapping an artist adds or drops your pick.
 * These are the group Lineup page's numbers, on the page people actually open.
 */

import { usePageLoading } from './motion/PetalLoader';
import { useEffect, useMemo, useRef, useState } from 'react';
import NothingYoursYet from './ui/NothingYoursYet';
import { useToast } from '../contexts/ToastContext';
import { isWelcomeDemo, WELCOME_FRIENDS, welcomeFriendPicks } from '../lib/welcomeDemo';
import { useEventRsvpParity } from '../hooks/useEventRsvpParity';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { isFestivalOnNow, refreshFestivalIfChanged } from '../lib/festivalOffline';
import { useIsRestoring, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Search, Users, X } from 'lucide-react';
import { useCurrentUserProfile } from '../contexts/CurrentUserProfileContext';
import { useFestivalSets, usePastFestivalSets } from '../hooks/queries/useFestivalSets';
import { isEventOver } from '../utils/eventCutoff';
import { useEventLineup, useMyArtistVotes } from '../hooks/queries/useEventLineup';
import { useEventDetail } from '../hooks/queries/useEventDetail';
import { useMyRsvps } from '../hooks/queries/useMyRsvps';
import { useMyEventGroups, useEventGroupVoters } from '../hooks/queries/useEventGroupVoters';
import useQuickPicks from '../hooks/useQuickPicks';
import {
  mergePerformances,
  mergeVotersByPerformance,
  rowVoteKeys,
} from '../utils/performanceGroups';
import { groupPrimaryLabel } from '../lib/proximity/data/userGroups';
import { groupHomePath } from '../config/productSurfaces';
import GroupAvatarStack from './lineup/GroupAvatarStack';
import IndexTile from './IndexTile';
import { EVENT_IMAGE_SLOT, getEventImageUrl } from '../lib/images/eventImageUrl';
import FestivalDownloadPill from './FestivalDownloadPill';
import FilterChip from './ui/FilterChip';
import ArtistRow from './lineup/ArtistRow';
import ArtistVotersModal from './lineup/ArtistVotersModal';
import LineupScheduleGrid from './lineup/LineupScheduleGrid';
import LineupViewList from './lineup/LineupViewList';
import LineupViewToggle from './lineup/LineupViewToggle';
import {
  festivalDaysFromLineup,
  formatDayAbbrev,
  formatDayLabelLong,
  rowSizeForArtist,
  voteKey,
} from '../utils/lineupFormat';
import {
  dayStartHour,
  groupByStage,
  lineupHasScheduleData,
  runningOrderHour,
} from '../utils/scheduleGrid';
import { resolveEventDisplayName } from '../utils/eventDisplayName';
import { DEFAULT_EVENT_TIMEZONE, formatEventDate } from '../utils/eventDateFormat';
import { warmSpotifyPlayer } from '../lib/spotifyPlayerPool';
import { goBackTo } from '../lib/navHistory';
import { PlayRowsContext, useHideNowPlaying } from './lineup/NowPlayingBar';
import { loadInBar } from '../lib/spotifyPlayerPool';
import { useNudge } from '../lib/moments';

/** The zone's UTC offset at an instant, in ms (positive east of UTC). */
function zoneOffsetMs(instantMs, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instantMs));
  const v = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return Date.UTC(+v.year, +v.month - 1, +v.day, +v.hour % 24, +v.minute, +v.second) - instantMs;
}

/**
 * A festival's start as an instant for isFestivalOnNow. event_datetime is already one; a date-only
 * event_date is midnight in the VENUE's zone (event showtimes belong in the venue's zone), not UTC
 * midnight - new Date('2026-09-26') is 5 PM the day before in Pacific, which read "on now" a night early.
 */
function festivalStartForOnNow(f) {
  if (f.event_datetime || !f.event_date) return f;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(f.event_date));
  if (!m) return f;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  try {
    const tz = f.timezone || DEFAULT_EVENT_TIMEZONE;
    const guess = Date.UTC(y, mo - 1, d);
    let t = guess - zoneOffsetMs(guess, tz);
    t = guess - zoneOffsetMs(t, tz); // settle across a DST change on the day
    return { ...f, event_datetime: new Date(t).toISOString() };
  } catch {
    return { ...f, event_datetime: new Date(y, mo - 1, d).toISOString() }; // unknown zone: the phone's own day
  }
}

export default function SetsPage() {
  const { eventId } = useParams();
  if (!eventId) return <SetsIndex />;
  return <SetsSchedule eventId={eventId} />;
}

/** The index: YOUR SETS - the upcoming festivals with a lineup you are going to - then MORE SETS, every other public
 * one, soonest first (Sam, 2026-10-26: the page was bare for anyone new; it was yours-only since 2026-10-12). The list
 * costs nothing extra - it was always loaded whole and filtered - and a browsed lineup is never saved to the phone
 * (lib/query/persister keepLineupFor); only yours download. */
function SetsIndex() {
  const navigate = useNavigate();
  const { session, authReady } = useCurrentUserProfile();
  const userId = session?.user?.id ?? null;
  const { data: festivals, isLoading: festivalsLoading, isError } = useFestivalSets();
  const { data: myRsvps = [], isLoading: rsvpsLoading } = useMyRsvps(userId, {
    enabled: authReady,
  });
  const [query, setQuery] = useState('');
  // Restoring the saved cache or waiting on auth is loading too, not "nothing yours" (see MapsIndex, 2026-10-12 audit).
  const restoring = useIsRestoring();
  const isLoading =
    restoring || !authReady || festivalsLoading || (Boolean(userId) && rsvpsLoading);
  const pageLoad = usePageLoading(isLoading);

  const goingList = useMemo(
    () =>
      userId
        ? myRsvps
            .filter((r) => r.status === 'going')
            .map((r) => r.event_id)
            .sort()
        : [],
    [myRsvps, userId],
  );
  const goingIds = useMemo(() => new Set(goingList), [goingList]);
  // PAST, at the bottom: the festivals you went to, latest first (Sam, 2026-10-27). It fills in after the rest.
  const { data: pastFestivals = [] } = usePastFestivalSets(goingList);
  const q = query.trim().toLowerCase();
  const matches = (f) => {
    if (!q) return true;
    const name = (resolveEventDisplayName(f) || f.event_name || '').toLowerCase();
    return name.includes(q) || (f.venues?.name || '').toLowerCase().includes(q);
  };
  const rows = (festivals ?? []).filter(matches);
  const yours = userId ? rows.filter((f) => goingIds.has(f.event_id)) : [];
  const more = rows.filter((f) => !userId || !goingIds.has(f.event_id));
  const past = pastFestivals.filter(matches);
  const hasAny = (festivals ?? []).length > 0 || pastFestivals.length > 0;

  // On a festival day, Sets opens on that festival (operator, 2026-09-23); backing out to this list says stay.
  const location = useLocation();
  const onNow = userId && !q ? yours.filter((f) => isFestivalOnNow(festivalStartForOnNow(f))) : [];
  if (!isLoading && !location.state?.stay && onNow.length === 1) {
    return (
      <Navigate
        to={`/sets/${encodeURIComponent(onNow[0].event_id)}`}
        state={{ fromIndex: true }}
        replace
      />
    );
  }

  // The whole page at once while there is nothing saved to show (Sam, 2026-10-20).
  if (pageLoad.show) return pageLoad.node;

  // Photo tiles, like Events and Maps (Sam, 2026-10-28). Only what you are going to downloads.
  const tile = (f, going = true) => {
    const name = resolveEventDisplayName(f) || f.event_name;
    return (
      <IndexTile
        key={f.event_id}
        photo={
          getEventImageUrl(f, EVENT_IMAGE_SLOT.HERO) || getEventImageUrl(f, EVENT_IMAGE_SLOT.CARD)
        }
        name={name}
        meta={[
          formatEventDate({
            event_datetime: f.event_datetime ?? f.event_date,
            timezone: f.timezone,
          }),
          f.venues?.name,
        ]
          .filter(Boolean)
          .join(' · ')}
        onOpen={() =>
          navigate(`/sets/${encodeURIComponent(f.event_id)}`, { state: { fromIndex: true } })
        }
        corner={
          going ? (
            <FestivalDownloadPill eventId={f.event_id} eventName={name} userId={userId} tile />
          ) : null
        }
      />
    );
  };

  return (
    <div className="list-index">
      <h1 className="sr-only">Set times</h1>
      {(hasAny || query) && (
        <div className="maps-page__search list-index__search">
          <Search size={15} aria-hidden="true" />
          <input
            type="search"
            value={query}
            placeholder="Search sets"
            aria-label="Search sets"
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button type="button" aria-label="Clear search" onClick={() => setQuery('')}>
              <X size={14} />
            </button>
          )}
        </div>
      )}

      {isError && rows.length === 0 ? (
        <p className="list-index__note">Could not load festivals. Pull to refresh.</p>
      ) : yours.length === 0 && more.length === 0 && past.length === 0 ? (
        q ? (
          <p className="list-index__note">No sets match.</p>
        ) : (
          <NothingYoursYet text="Set times show up for festivals you're going to." />
        )
      ) : (
        <>
          {yours.length > 0 && (
            <>
              <h2 className="list-index__label">Your sets</h2>
              <div className="event-tiles index-tiles">{yours.map((f) => tile(f))}</div>
            </>
          )}
          {more.length > 0 && (
            <>
              <h2 className="list-index__label">More sets</h2>
              <div className="event-tiles index-tiles">{more.map((f) => tile(f, false))}</div>
            </>
          )}
          {past.length > 0 && (
            <>
              <h2 className="list-index__label">Past</h2>
              <div className="event-tiles index-tiles">{past.map((f) => tile(f, false))}</div>
            </>
          )}
        </>
      )}
    </div>
  );
}

/** List or Schedule, once set times are out; this app session. The stage grid leads (operator, 2026-09-23). */
const lastSetsView = { value: 'schedule' };
/** The day last viewed per festival, for this app session. */
const lastDayByEvent = new Map();

/** One festival's schedule — the best view its published data supports. */
function SetsSchedule({ eventId }) {
  const navigate = useNavigate();
  const location = useLocation();
  // The welcome flow's Sets (lib/welcomeDemo): Portola as a show - picks stay on this screen, nothing is saved, no group
  // card or overlap, no Back.
  const demo = isWelcomeDemo(location.search);
  // Opened from a group's page: that group's picks, and Back returns to the group.
  const groupFromLink = new URLSearchParams(location.search).get('group');
  const backTo = location.state?.backTo ?? '/sets';
  const { session, authReady } = useCurrentUserProfile();
  const userId = session?.user?.id ?? null;
  // isPending, not isLoading: while the saved cache restores at start-up the query waits without fetching, and isLoading
  // was false - the page said "No lineup announced yet" for Head Trip until the network answered (2026-10-07).
  const { data: rawLineup = [], isPending: isLoading } = useEventLineup(eventId);
  const lineupLoad = usePageLoading(isLoading); // no lineup saved yet: the loader is the whole page
  // A b2b is one row, one pick, one grid block (mergePerformances).
  const lineup = useMemo(() => mergePerformances(rawLineup), [rawLineup]);
  // Boot the Spotify player once while the lineup is on screen, so even the first artist sheet opens without the wait
  // (spotifyPlayerPool: one player per session, switched per artist).
  const warmSpotifyId = rawLineup.find((a) => a.spotify_artist_id)?.spotify_artist_id ?? null;
  useEffect(() => {
    if (warmSpotifyId) warmSpotifyPlayer(warmSpotifyId);
  }, [warmSpotifyId]);
  // With signal, opening a festival's sets picks up changed set times and a newly published map (at most every 2 min).
  const refreshClient = useQueryClient();
  useEffect(() => {
    void refreshFestivalIfChanged(refreshClient, eventId, userId);
  }, [refreshClient, eventId, userId]);
  const { data: eventGroups = [] } = useMyEventGroups(eventId, userId, { authReady });
  const [groupPick, setGroupPick] = useState(groupFromLink);
  const group = eventGroups.find((g) => g.id === groupPick) ?? eventGroups[0] ?? null;
  const {
    members,
    votersByArtist: rawVoters,
    votersReady,
    totalMemberCount,
  } = useEventGroupVoters({ eventId, groupId: group?.id ?? null, userId });
  const mergedVoters = useMemo(
    () => mergeVotersByPerformance(rawVoters, lineup),
    [rawVoters, lineup],
  );
  // Overlap only means something with someone to overlap with; a group of one shows your picks, not "1/1". The welcome
  // show has its four sample friends, with fixed picks (welcomeFriendPicks), so the group side of picking is there to see.
  const showOverlap = demo || (Boolean(group) && totalMemberCount > 1);
  const groupSize = demo ? WELCOME_FRIENDS.length + 1 : totalMemberCount;
  const demoVoters = useMemo(() => (demo ? welcomeFriendPicks(lineup) : null), [demo, lineup]);
  // The index row names the festival and is already cached when you arrive from it.
  // useEventDetail is the deep-link fallback, and only a fallback: it returns null for
  // an event_type='festival' row that is not festival_ready, which is how this page
  // came to be titled "Event" for CRSSD (2026-09-22).
  const { data: indexRows = [] } = useFestivalSets();
  const indexRow = useMemo(
    () => indexRows.find((r) => r.event_id === eventId) ?? null,
    [indexRows, eventId],
  );
  const { data: detail } = useEventDetail(eventId);
  const event = indexRow ?? detail;
  const { data: savedVotes = [] } = useMyArtistVotes(eventId, userId);
  const [openArtist, setOpenArtist] = useState(null);
  useHideNowPlaying(Boolean(openArtist)); // the artist sheet covers the bottom: the app's bar tucks away under it

  const days = useMemo(() => festivalDaysFromLineup(lineup), [lineup]);
  // The day you were on survives a tab switch (operator, 2026-09-23: come back to exactly where you were).
  const [day, setDayState] = useState(() => lastDayByEvent.get(eventId) ?? null);
  const setDay = (d) => {
    lastDayByEvent.set(eventId, d);
    setDayState(d);
  };
  const activeDay = day ?? days[0] ?? null;

  const selectedKeys = useMemo(
    () => new Set(savedVotes.map((v) => voteKey(v.artist_id, v.performance_day))),
    [savedVotes],
  );
  // Picks update on tap and save in the background (useQuickPicks); the grid, rows and the artist sheet all read them.
  const picks = useQuickPicks({
    eventId,
    userId,
    groupId: group?.id ?? null,
    savedKeys: selectedKeys,
  });
  // Rows, grid and sheet speak (artistId, day) of a row's lead artist; a b2b's row stands for all its artists.
  const keysByLead = useMemo(() => {
    const m = new Map();
    lineup.forEach((row) => {
      if (row.members) m.set(voteKey(row.artist_id, row.performance_day), rowVoteKeys(row));
    });
    return m;
  }, [lineup]);
  const keysFor = (artistId, day) =>
    keysByLead.get(voteKey(artistId, day)) ?? [voteKey(artistId, day)];
  const [demoPicks, setDemoPicks] = useState(() => new Set());
  const pickedNow = demo ? demoPicks : picks.pickedKeys;
  const isRowPicked = (artistId, day) => keysFor(artistId, day).some((k) => pickedNow.has(k));
  // Picks are for people going (they feed the group's overlap). Not going - or signed out - the + says so in one line
  // with a Join that starts the usual Join right here (Sam's B, 2026-10-26: moving to the event page was confusing).
  const { data: myRsvps = [], refetch: refetchRsvps } = useMyRsvps(userId, { enabled: authReady });
  const going =
    Boolean(userId) && myRsvps.some((r) => r.event_id === eventId && r.status === 'going');
  const { showToast } = useToast();
  const { handleEventCardRSVP, parityModals } = useEventRsvpParity({
    session,
    showToast,
    getEventForCutoff: async () => event ?? null,
    updateLocalRsvp: () => {},
    refetchRsvps,
  });
  // Over: the schedule and your picks stay to look back on (ratings will hang off them), but picks are closed.
  const over = !demo && Boolean(event) && isEventOver(event);
  const quickToggle = over
    ? () => showToast('This festival is over')
    : demo
      ? (artistId, day) =>
          setDemoPicks((prev) => {
            const next = new Set(prev);
            keysFor(artistId, day).forEach((k) => (next.has(k) ? next.delete(k) : next.add(k)));
            return next;
          })
      : going
        ? (artistId, day) => picks.toggleKeys(keysFor(artistId, day))
        : () =>
            showToast('Join to pick sets', 5000, {
              action: {
                label: 'Join',
                onClick: () => void handleEventCardRSVP(eventId, 'going', null),
              },
            });
  // Your own pick shows in the group's count the moment you tap, not after the save round trip (operator, 2026-09-23:
  // "there's a delay from when I vote"). Every row's voters: the saved ones minus you, plus you if picked now.
  const me = useMemo(() => {
    const m = members.find((x) => x.user_id === userId);
    return userId
      ? {
          id: userId,
          display_name: m?.profile?.display_name || 'You',
          profile_photo_url: m?.profile?.profile_photo_url,
        }
      : null;
  }, [members, userId]);
  const votersByArtist = useMemo(() => {
    if (demoVoters) {
      const you = me ?? { id: 'you', display_name: 'You' };
      const out = {};
      lineup.forEach((row) => {
        const theirs = demoVoters[row.artist_id] || [];
        out[row.artist_id] = isRowPicked(row.artist_id, row.performance_day)
          ? [...theirs, you]
          : theirs;
      });
      return out;
    }
    if (!me || !mergedVoters) return mergedVoters;
    const out = {};
    lineup.forEach((row) => {
      const others = (mergedVoters[row.artist_id] || []).filter((v) => v.id !== me.id);
      const mine = rowVoteKeys(row).some((k) => picks.pickedKeys.has(k));
      out[row.artist_id] = mine ? [...others, me] : others;
    });
    return out;
  }, [mergedVoters, lineup, picks.pickedKeys, me, demoVoters, demoPicks]); // eslint-disable-line react-hooks/exhaustive-deps
  // The grid checks the lead's key: count a b2b as picked when any of its artists is.
  const gridPickedKeys = useMemo(() => {
    const out = new Set(pickedNow);
    keysByLead.forEach((keys, lead) => {
      if (keys.some((k) => pickedNow.has(k))) out.add(lead);
    });
    return out;
  }, [pickedNow, keysByLead]);

  const dayRows = useMemo(
    () => (activeDay ? lineup.filter((r) => r.performance_day === activeDay) : lineup),
    [lineup, activeDay],
  );
  // An all-weekend stage: a festival's artists announced for a stage with no day and no time (Floating Points'
  // Sunflower Sound System at Head Trip, "at the controls" all weekend). They belong to every day, so they show under
  // their stage on whichever day you pick - never given a time nobody published (Sam, 2026-10-07: they did not show at all).
  const allWeekend = useMemo(() => {
    if (!activeDay) return [];
    const byStage = new Map();
    for (const r of lineup) {
      if (r.performance_day || r.set_start_time || !r.stage) continue;
      if (!byStage.has(r.stage)) byStage.set(r.stage, []);
      byStage.get(r.stage).push(r);
    }
    return [...byStage].map(([stage, rows]) => ({ stage, rows }));
  }, [lineup, activeDay]);
  const hasTimes = lineupHasScheduleData(lineup, activeDay);
  const [setsView, setSetsViewState] = useState(() => lastSetsView.value);
  const setSetsView = (v) => {
    lastSetsView.value = v;
    setSetsViewState(v);
  };
  // The day latest first, so the closing sets (the headliners) lead and the openers sit at the bottom (operator,
  // 2026-09-28); ties by stage. Sets without a time sit at the end.
  const timedRows = useMemo(() => {
    // Small-hours sets belong to the night before, however late it runs (dayStartHour: the grid's own rule).
    const anchor = dayStartHour(dayRows);
    const hour = (r) => runningOrderHour(r, anchor);
    return [...dayRows].sort((a, b) => {
      const ha = hour(a);
      const hb = hour(b);
      if (ha !== hb) {
        if (ha === Infinity) return 1;
        if (hb === Infinity) return -1;
        return hb - ha;
      }
      return String(a.stage || '').localeCompare(String(b.stage || ''));
    });
  }, [dayRows]);
  // Before set times, one list top to bottom: every act your group picked, most-picked first, then everyone else in
  // the usual stage/billing order (operator, 2026-09-23). The counts are taken once when the page opens and held while
  // you are on it, so a row never jumps as you pick; leave and come back and it re-ranks.
  const rankKey = `${eventId}|${group?.id ?? ''}`;
  const rankRef = useRef({ key: null, snap: null });
  if (rankRef.current.key !== rankKey) rankRef.current = { key: rankKey, snap: null };
  if (!rankRef.current.snap && showOverlap && (votersReady || demo) && lineup.length) {
    const snap = new Map();
    lineup.forEach((row) => {
      const n = (votersByArtist[row.artist_id] || []).length;
      if (n) snap.set(row.artist_id, n);
    });
    rankRef.current.snap = snap;
  }
  const rank = rankRef.current.snap;
  const pickedFirst = useMemo(() => {
    if (!rank || rank.size === 0) return [];
    const at = new Map(dayRows.map((r, i) => [r, i]));
    return dayRows
      .filter((r) => rank.has(r.artist_id))
      .sort((a, b) => rank.get(b.artist_id) - rank.get(a.artist_id) || at.get(a) - at.get(b));
  }, [dayRows, rank]);
  const restRows = useMemo(
    () => (pickedFirst.length ? dayRows.filter((r) => !rank.has(r.artist_id)) : dayRows),
    [dayRows, pickedFirst, rank],
  );
  const stages = useMemo(() => groupByStage(restRows).filter((s) => s.stage !== 'TBA'), [restRows]);
  const hasStages = useMemo(() => dayRows.some((r) => r.stage && r.stage !== 'TBA'), [dayRows]);
  const overlapProps = showOverlap ? { votersByArtist, totalVoters: groupSize } : {};
  const venue = Array.isArray(event?.venues) ? event?.venues[0] : event?.venues;
  const title = resolveEventDisplayName(event) || event?.event_name || 'Set times';

  // The first-time path (lib/moments): the first set's + glows until the first pick.
  const pickGlow = useNudge('pick', (going && !over) || demo); // the first-pick glow only where a pick can be made

  // A set's one tap (the schedule has no room for a play button): its artist goes into the player, ready but not
  // playing, so a stray tap never starts sound (Sam, 2026-10-23). A set Spotify does not know has nothing to load
  // (Sam, 2026-10-26: not the old sheet either).
  const previewSet = (row) => {
    const players = (row.members ?? [row])
      .filter((m) => m.spotify_artist_id)
      .map((m) => ({
        spotifyId: m.spotify_artist_id,
        name: m.canonical_artist_name || m.artist_name,
      }));
    if (players.length) loadInBar(voteKey(row.artist_id, row.performance_day), players);
  };

  if (lineupLoad.show) return lineupLoad.node;

  return (
    <PlayRowsContext.Provider value>
      <div className="sets-page">
        <div className="sets-page__head">
          {!demo && (
            <button
              type="button"
              className="sets-page__back"
              aria-label={
                backTo === '/sets'
                  ? 'Back to festivals'
                  : backTo.startsWith('/events/')
                    ? 'Back to event'
                    : 'Back to group'
              }
              onClick={() =>
                goBackTo(
                  navigate,
                  backTo,
                  backTo === '/sets' ? { state: { stay: true } } : undefined,
                )
              }
            >
              <ChevronLeft size={18} aria-hidden="true" />
            </button>
          )}
          <div className="sets-page__title">
            <h1>{title}</h1>
            {venue?.name && <p>{[venue.name, venue.city].filter(Boolean).join(' · ')}</p>}
          </div>
        </div>

        {!demo &&
          userId &&
          lineup.length > 0 &&
          (group ? (
            <div className="sets-page__group">
              <Link to={groupHomePath(group.id)} className="sets-page__group-main">
                <GroupAvatarStack
                  people={members.map((m) => ({
                    id: m.user_id,
                    display_name: m.profile?.display_name,
                    profile_photo_url: m.profile?.profile_photo_url,
                  }))}
                  max={4}
                  size="xxs"
                  overlapPx={-6}
                />
                <span className="sets-page__group-text">
                  <strong>{groupPrimaryLabel(group)}</strong>
                  <span>
                    {showOverlap ? 'Tap + to add your picks' : 'Invite friends to compare picks'}
                  </span>
                </span>
                <ChevronRight size={15} aria-hidden="true" />
              </Link>
              {eventGroups.length > 1 && (
                <div className="sets-page__group-switch" role="group" aria-label="Show picks for">
                  {eventGroups.map((g) => (
                    <FilterChip
                      key={g.id}
                      selected={g.id === group.id}
                      onClick={() => setGroupPick(g.id)}
                      className="lineup-chip !min-h-0 !rounded-full !px-3 !py-1 !text-micro"
                    >
                      {groupPrimaryLabel(g)}
                    </FilterChip>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <Link
              to={`/events/${encodeURIComponent(eventId)}`}
              className="sets-page__group sets-page__group--empty"
            >
              <Users size={16} aria-hidden="true" />
              <span>Start a group to compare picks</span>
              <ChevronRight size={15} aria-hidden="true" />
            </Link>
          ))}

        {/* The day chips, as they were, but right above Schedule / List - next to what they filter, not up by the
          title where they were missed (Sam, 2026-10-07: "keep it the old way", just more visible). */}
        {days.length > 1 && (
          <div className="sets-page__days">
            {days.map((d) => (
              <FilterChip
                key={d}
                selected={activeDay === d}
                onClick={() => setDay(d)}
                pill="lg"
                className="lineup-chip"
              >
                {formatDayAbbrev(d)}
              </FilterChip>
            ))}
          </div>
        )}
        {activeDay && days.length > 1 && (
          <p className="sets-page__day-label">{formatDayLabelLong(activeDay)}</p>
        )}

        {lineup.length === 0 ? (
          <p className="list-index__note">No lineup announced yet.</p>
        ) : hasTimes ? (
          <>
            {/* Once times are out: the stage grid (the default), or the day as a list, latest set first (time, stage, your
              group's overlap, +/-) - a two-way pill switches; the choice is remembered for the session. */}
            <LineupViewToggle
              view={setsView}
              onChange={setSetsView}
              modes={['schedule', 'list']}
              className="!mx-0 !mb-3"
            />
            {setsView === 'schedule' && quickToggle && (
              <p className="sets-page__hint">Double-tap a set to add it to your picks.</p>
            )}
            {setsView === 'list' ? (
              <section className="sets-page__timed" aria-label="Sets, latest first">
                {timedRows.map((row, i) => (
                  <ArtistRow
                    nudge={i === 0 ? pickGlow : ''}
                    key={`timed|${row.artist_id}|${row.performance_day ?? ''}`}
                    artist={row}
                    size={rowSizeForArtist(row)}
                    layout="view"
                    showTime
                    voters={showOverlap ? votersByArtist[row.artist_id] : undefined}
                    totalVoters={showOverlap ? groupSize : undefined}
                    onPress={() => setOpenArtist(row)}
                    onQuickToggle={quickToggle}
                    picked={isRowPicked(row.artist_id, row.performance_day)}
                  />
                ))}
              </section>
            ) : (
              <LineupScheduleGrid
                lineup={lineup}
                day={activeDay}
                selectedKeys={gridPickedKeys}
                votersByArtist={showOverlap ? votersByArtist : undefined}
                groupSize={showOverlap ? groupSize : null}
                readOnly
                onPress={previewSet}
                onDoubleTap={quickToggle}
                allDayStages={allWeekend}
              />
            )}
          </>
        ) : (
          <>
            {/* Say what is missing, once, at the top — an attendee looking for a set time
              should not have to infer it from the absence of a grid. */}
            <p className="sets-page__pending">Set times aren&apos;t out yet.</p>
            {pickedFirst.length > 0 && (
              <section className="sets-page__stage" aria-label="Your group's picks">
                <h2 className="sets-page__stage-name">Your group&apos;s picks</h2>
                {pickedFirst.map((row) => (
                  <ArtistRow
                    key={`picked|${row.artist_id}|${row.performance_day ?? ''}`}
                    artist={row}
                    size={rowSizeForArtist(row)}
                    layout="view"
                    voters={votersByArtist[row.artist_id]}
                    totalVoters={groupSize}
                    onPress={() => setOpenArtist(row)}
                    onQuickToggle={quickToggle}
                    picked={isRowPicked(row.artist_id, row.performance_day)}
                  />
                ))}
              </section>
            )}
            {hasStages ? (
              stages.map(({ stage, rows }, si) => (
                <section key={stage} className="sets-page__stage">
                  <h2 className="sets-page__stage-name">{stage}</h2>
                  {/* The row's subtext in "view" layout is the stage, which the section
                    header already says. Blank it for the row, keep it on the artist the
                    modal opens. */}
                  {rows.map((row, ri) => (
                    <ArtistRow
                      nudge={si === 0 && ri === 0 ? pickGlow : ''}
                      key={`${row.artist_id}|${row.performance_day ?? ''}`}
                      artist={{ ...row, stage: null }}
                      size={rowSizeForArtist(row)}
                      layout="view"
                      voters={showOverlap ? votersByArtist[row.artist_id] : undefined}
                      totalVoters={showOverlap ? groupSize : undefined}
                      onPress={() => setOpenArtist(row)}
                      onQuickToggle={quickToggle}
                      picked={isRowPicked(row.artist_id, row.performance_day)}
                    />
                  ))}
                </section>
              ))
            ) : (
              <LineupViewList
                lineup={restRows}
                singleDay={activeDay}
                onPress={setOpenArtist}
                {...overlapProps}
                onQuickToggle={quickToggle}
                isPicked={isRowPicked}
              />
            )}
          </>
        )}

        {!isLoading &&
          !(hasTimes && setsView === 'schedule') &&
          allWeekend.map(({ stage, rows }) => (
            <section
              key={`allweekend|${stage}`}
              className="sets-page__stage"
              aria-label={`${stage}, all weekend`}
            >
              <h2 className="sets-page__stage-name">{stage}</h2>
              <p className="sets-page__all-weekend">All weekend · no set times</p>
              {rows.map((row) => (
                <ArtistRow
                  key={`allweekend|${row.artist_id}`}
                  artist={{ ...row, stage: null }}
                  size={rowSizeForArtist(row)}
                  layout="view"
                  voters={showOverlap ? votersByArtist[row.artist_id] : undefined}
                  totalVoters={showOverlap ? groupSize : undefined}
                  onPress={() => setOpenArtist(row)}
                  onQuickToggle={quickToggle}
                  picked={isRowPicked(row.artist_id, row.performance_day)}
                />
              ))}
            </section>
          ))}

        <ArtistVotersModal
          artist={openArtist}
          voters={showOverlap && openArtist ? votersByArtist[openArtist.artist_id] || [] : []}
          viewerId={userId}
          totalMemberCount={showOverlap ? groupSize : undefined}
          context={showOverlap ? 'group' : 'event'}
          selected={
            openArtist ? isRowPicked(openArtist.artist_id, openArtist.performance_day) : false
          }
          onToggleVote={quickToggle}
          onClose={() => setOpenArtist(null)}
        />
        {parityModals}
      </div>
    </PlayRowsContext.Provider>
  );
}
